import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Space, Steps } from 'antd';

import PPPageHeader from '../../../components/pageHeader/PPPageHeader';
import useStore from '../../../store/useStore';
import { AddOrderDeliveryProvider } from '../../orderDelivery/Add/AddOrderDeliveryContext';
import { expressDraftKey, pickDraft, readDraft, useClearDraftOnLeave, writeDraft } from './draft';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import useAddExpressDeliveryStore from './store';

const routes = [
  { path: '/merchant/order/delivery', breadcrumbName: 'Хүргэлтүүд' },
  { path: 'first', breadcrumbName: 'Шинэ хүргэлтийн захиалга' },
];

const STEPS = [
  { title: 'Бараа', description: 'Барааны мэдээлэл' },
  { title: 'Хянах', description: 'Мэдээлэл хянах' },
  { title: 'Төлбөр төлөх', description: 'Хүргэлтийн төлбөр' },
];

const AddExpressDelivery = () => {
  const history = useHistory();
  const location = useLocation();

  const resetStore = useAddExpressDeliveryStore((state) => state.resetStore);

  /**
   * Алхмыг URL-ийн `?step=1..3` агуулна — refresh болон browser-ийн
   * back/forward ажиллана.
   */
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  const urlStep =
    Number.isInteger(stepParam) && stepParam >= 1 ? Math.min(stepParam - 1, STEPS.length - 1) : 0;

  // Эхний рендер дээр л: draft-д дата байхгүй алхам руу шууд орохыг хориглоно
  // (жиш нь шинэ session дээр `?step=3` гэж гараар оровол 1-р алхмаас эхэлнэ)
  const isMounted = useRef(false);
  /**
   * Draft-ыг merchant-аар салгана — merchant солиход (нэг таб дотор reload
   * болдог тул) өмнөх merchant-ийн сонголт шинэ merchant дээр сэргэхгүй.
   */
  const merchantCode = useStore((state) => state.user.merchantCode);
  const draftKey = expressDraftKey(merchantCode);
  const draft = useRef(readDraft(draftKey)).current;

  /**
   * Draft-ыг store-д ЭХНИЙ РЕНДЕРИЙН ҮЕД буулгана: Step1-ийн `<Form>` нь
   * `initialValues`-ыг зөвхөн mount дээр уншдаг тул effect-д хийвэл сэргээсэн
   * утга формд тусахгүй. `ref` нь нэг л удаа ажиллахыг хангана.
   */
  const isHydrated = useRef(false);
  if (!isHydrated.current) {
    isHydrated.current = true;
    if (Object.keys(draft).length) useAddExpressDeliveryStore.getState().hydrate(draft);
  }

  useEffect(() => {
    // Store-ын өөрчлөлт бүрд draft-ыг шинэчилнэ — refresh дээр сэргэнэ
    const unsubscribe = useAddExpressDeliveryStore.subscribe((state) =>
      writeDraft(draftKey, pickDraft(state))
    );
    return unsubscribe;
  }, [draftKey]);

  // Хуудсаас гармагц бөглөсөн мэдээлэл цэвэрлэгдэнэ (refresh дээр сэргэсэн хэвээр)
  useClearDraftOnLeave(draftKey);

  /**
   * Store нь модуль хэмжээний singleton тул хуудас unmount болоход өөрөө
   * тэглэгддэггүй. Draft-тай ижил хугацаанд амьдрахын тулд гарахад тэглэнэ —
   * эс бөгөөс дараа дахин орж ирэхэд өмнөх захиалгын мэдээлэл сэргэнэ.
   */
  useEffect(() => () => resetStore(), [resetStore]);

  /**
   * `step1Values.orders` нь 1-р алхмыг БАТАЛСАН үед, `payment` нь захиалга
   * үүсч төлбөрийн баримт гарсан үед л бичигддэг тул алхмын зөвшөөрлийг
   * тэднээр шалгана.
   */
  const draftStep1 = draft.step1Values ?? {};
  const maxAllowedStep = draftStep1.payment ? 2 : draftStep1.orders?.length ? 1 : 0;
  const current = isMounted.current ? urlStep : Math.min(urlStep, maxAllowedStep);

  /**
   * Алхам солихдоо шинэ history бичлэг үүсгэнэ — browser-ийн back дарахад
   * өмнөх алхам руу буцна. Зөвхөн зөвшөөрөгдөөгүй алхмыг залруулахдаа
   * `replace` хийнэ (буцах товч дахин тэр алхам руу оруулахгүй).
   */
  const goToStep = (step, { replace = false } = {}) => {
    // `tab` зэрэг бусад параметрийг хөндөхгүй — алхам хооронд явахад таб хэвээрээ
    const params = new URLSearchParams(location.search);
    params.set('step', String(step + 1));

    const target = { pathname: location.pathname, search: `?${params.toString()}` };
    return replace ? history.replace(target) : history.push(target);
  };

  const nextStep = () => goToStep(current + 1);
  const prevStep = () => goToStep(current - 1);

  useEffect(() => {
    isMounted.current = true;
    if (urlStep > maxAllowedStep) goToStep(maxAllowedStep, { replace: true });
    //eslint-disable-next-line
  }, []);

  const stepContent = [
    <Step1 key="step1" nextStep={nextStep} />,
    <Step2 key="step2" prevStep={prevStep} nextStep={nextStep} />,
    <Step3 key="step3" prevStep={prevStep} />,
  ];

  return (
    <div>
      <PPPageHeader
        title="Шинэ хүргэлтийн захиалга"
        breadcrumb={{ routes }}
        onBack={() => history.push('/merchant/order/delivery')}
      />

      <AddOrderDeliveryProvider>
        <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
          <Card size="middle">
            {/**
             * Гарчиг дээр дарж ӨМНӨХ алхам руу буцаж болно. Урагшаа алхам нь
             * disabled — мэдээллийг батлахгүйгээр цааш харайхыг хориглоно.
             */}
            <Steps
              current={current}
              responsive
              size="small"
              onChange={(step) => step < current && goToStep(step)}
              items={STEPS.map((step, index) => ({ ...step, disabled: index > current }))}
            />
          </Card>

          {stepContent[current]}
        </Space>
      </AddOrderDeliveryProvider>
    </div>
  );
};

export default AddExpressDelivery;
